if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.isDockkitGallery()) {
          this.dockkitVariantChangeHandler = () => window.setTimeout(() => this.filterDockkitMedia(), 0);
          document.addEventListener('change', this.dockkitVariantChangeHandler);
          window.setTimeout(() => this.filterDockkitMedia(), 0);
        }
      }

      disconnectedCallback() {
        if (this.dockkitVariantChangeHandler) {
          document.removeEventListener('change', this.dockkitVariantChangeHandler);
        }
      }

      isDockkitGallery() {
        return window.location.pathname.includes(
          '/products/greenfield-boat-fender-dock-kit-2-pack-with-bungee-dock-lines-and-rope-locks'
        );
      }

      getDockkitColor() {
        const selectedColor = Array.from(document.querySelectorAll('input[type="radio"]:checked'))
          .map((input) => input.value)
          .find((value) => ['Black', 'Royal Blue', 'Red', 'Yellow'].includes(value));

        return selectedColor || 'Black';
      }

      getDockkitSize() {
        return (
          Array.from(document.querySelectorAll('input[type="radio"]:checked'))
            .map((input) => input.value)
            .find((value) => ['4.5 in', '5.5 in'].includes(value)) || '4.5 in'
        );
      }

      filterDockkitMedia() {
        if (!this.isDockkitGallery()) return;

        const colorPatterns = {
          Black: ['main-black', 'scale-black', 'dockkit-b-'],
          'Royal Blue': ['main-blue', 'scale-blue', 'dockkit-r-'],
          Red: ['main-red', 'scale-red', 'dockkit-rd-'],
          Yellow: ['main-yellow', 'scale-yellow', 'dockkit-y-'],
        };
        const selectedColor = this.getDockkitColor();
        const selectedSize = this.getDockkitSize();
        const patterns = colorPatterns[selectedColor];
        const matchesSelectedColor = (item) => {
          const imageSource = item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
          const normalizedSource = imageSource.toLowerCase();
          if (!patterns.some((pattern) => normalizedSource.includes(pattern))) return false;

          const isSmallScaleImage = normalizedSource.includes('16inch') || normalizedSource.includes('16-inch');
          const isLargeScaleImage = normalizedSource.includes('20inch') || normalizedSource.includes('20-inch');
          if (selectedSize === '4.5 in' && isLargeScaleImage) return false;
          if (selectedSize === '5.5 in' && isSmallScaleImage) return false;

          if (selectedColor === 'Yellow') {
            const isOriginalLargeFeaturedImage = normalizedSource.includes('/dockkit-y-lfst1.jpg');
            if (isOriginalLargeFeaturedImage) return false;
          }

          return true;
        };

        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((item) => {
          item.hidden = !matchesSelectedColor(item);
        });
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((item) => {
          item.hidden = !matchesSelectedColor(item);
        });

        const mediaOrder = (item) => {
          const imageSource = (item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '').toLowerCase();
          if (imageSource.includes('main-')) return 0;
          const numberedImage = imageSource.match(/(?:lfst|lifst)(\d+)/);
          if (numberedImage) return Number(numberedImage[1]);
          if (imageSource.includes('info')) return 90;
          if (imageSource.includes('scale')) return 100;
          return 80;
        };
        const reorderMedia = (container, selector) => {
          if (!container) return;
          Array.from(container.querySelectorAll(selector))
            .sort((left, right) => mediaOrder(left) - mediaOrder(right))
            .forEach((item) => container.append(item));
        };

        reorderMedia(this.elements.viewer?.querySelector('ul'), '[data-media-id]');
        reorderMedia(this.elements.thumbnails?.querySelector('ul'), '[data-target]');

        if (this.elements.viewer?.slider) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.slider) this.elements.thumbnails.resetPages();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.filterDockkitMedia();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
